import { motion } from "framer-motion";
import { 
  Search, 
  TrendingUp, 
  RotateCcw, 
  Clock, 
  MessageCircle,
  ShieldCheck 
} from "lucide-react";

const FeaturesSection = () => {
  const features = [
    {
      icon: Search,
      title: "Best Loads First",
      description: "MyLane scans multiple load boards to find the highest paying loads for your specific lanes and preferences.",
      color: "bg-blue-500",
    },
    {
      icon: TrendingUp,
      title: "Smart Negotiation",
      description: "We negotiate better rates with brokers on your behalf. Most drivers see 10-20% higher rates.",
      color: "bg-green-500",
    },
    {
      icon: RotateCcw,
      title: "Backhaul Optimization",
      description: "Find return loads and avoid empty miles. Never deadhead when there's freight available.",
      color: "bg-purple-500",
    },
    {
      icon: Clock,
      title: "24/7 Dispatch Support",
      description: "Need a load at 3am? No problem. MyLane is available whenever you need to find freight.",
      color: "bg-accent",
    },
    {
      icon: MessageCircle,
      title: "Simple Communication",
      description: "Everything happens through WhatsApp. No apps to learn, no portals to navigate.",
      color: "bg-teal-500",
    },
    {
      icon: ShieldCheck,
      title: "Vetted Brokers",
      description: "We only work with reliable, verified brokers with good payment history.",
      color: "bg-primary",
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 },
    },
  };

  return (
    <section
      id="features"
      className="py-20 md:py-32 bg-white"
      data-testid="features-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="inline-block bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-bold uppercase tracking-wider mb-4">
            Features
          </span>
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-primary tracking-tight uppercase mb-6">
            Built for Owner Operators
          </h2>
          <p className="text-lg md:text-xl text-slate-600 max-w-2xl mx-auto">
            Every feature is designed with independent truckers in mind.
          </p>
        </motion.div>

        {/* Bento Grid Features */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {features.map((feature, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              whileHover={{ y: -5 }}
              className="feature-card bg-white p-8 rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition-all group"
              data-testid={`feature-card-${index}`}
            >
              <div className={`w-14 h-14 rounded-xl ${feature.color} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}>
                <feature.icon className="w-7 h-7 text-white" />
              </div>
              <h3 className="font-heading text-2xl font-bold text-primary mb-3">
                {feature.title}
              </h3>
              <p className="text-slate-600 leading-relaxed">
                {feature.description}
              </p>
            </motion.div>
          ))}
        </motion.div>

        {/* Stats Row */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6"
        >
          {[
            { value: "500+", label: "Active Drivers" },
            { value: "15K+", label: "Loads Dispatched" },
            { value: "98%", label: "Driver Satisfaction" },
            { value: "$2M+", label: "Saved for Drivers" },
          ].map((stat, index) => (
            <div
              key={index}
              className="text-center p-6 bg-slate-50 rounded-xl"
              data-testid={`stat-${index}`}
            >
              <p className="font-heading text-3xl md:text-4xl font-bold text-accent mb-1">
                {stat.value}
              </p>
              <p className="text-slate-600 font-medium">{stat.label}</p>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default FeaturesSection;
